// src/components/ProgressTracker.tsx
// Remove unused import and fix missing dependency:
import React, { useState, useEffect, useMemo } from 'react';
import { X, Download, Clock } from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import type { Job } from '../types';
import './ProgressTracker.css';

const JobCard: React.FC<{ job: Job }> = ({ job }) => {
  const { cancelJob, removeJob } = useAppContext();
  const [isHiding, setIsHiding] = useState(false);

  const handleDismiss = () => {
    setIsHiding(true);
    setTimeout(() => removeJob(job.job_id), 200);
  };

  const handleCancel = () => {
    cancelJob(job.job_id);
  };

  // Auto-download completed exports
  useEffect(() => {
    if (job.status === 'completed' && (job.job_type === 'export' || (job.job_type as string) === 'enrichment_export') && (job.result?.signed_download_url || job.result?.download_url)) {
      window.open(job.result.signed_download_url || job.result.download_url, '_blank');
    }
  }, [job.status, job.job_type, job.result?.signed_download_url, job.result?.download_url]);

  // Auto-dismiss completed jobs
  useEffect(() => {
    if (!['completed', 'failed', 'canceled'].includes(job.status)) {
      return;
    }
    
    const timer = setTimeout(() => {
      handleDismiss();
    }, job.status === 'canceled' ? 2000 : 5000);
    
    return () => {
      clearTimeout(timer);
    };
  }, [job.status, job.job_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const jobInfo = useMemo(() => {
    const getTitle = () => {
      const titles = {
        'export': { 
          pending: 'Preparing Export', 
          running: 'Exporting Data', 
          completed: 'Export Complete', 
          failed: 'Export Failed', 
          canceled: 'Export Canceled' 
        },
        'add_to_list': { 
          pending: 'Preparing List Update', 
          running: 'Adding to List', 
          completed: 'Added to List', 
          failed: 'Update Failed', 
          canceled: 'Update Canceled' 
        },
        'contacts_unlock': { 
          pending: 'Preparing Contacts', 
          running: 'Adding Contacts', 
          completed: 'Contacts Added', 
          failed: 'Add Contacts Failed', 
          canceled: 'Add Contacts Canceled' 
        },
        'delete_contacts': { 
          pending: 'Preparing Deletion', 
          running: 'Deleting Contacts', 
          completed: 'Deletion Complete', 
          failed: 'Deletion Failed', 
          canceled: 'Deletion Canceled' 
        },
        'remove_from_list': { 
          pending: 'Preparing Removal', 
          running: 'Removing from List', 
          completed: 'Removal Complete', 
          failed: 'Removal Failed', 
          canceled: 'Removal Canceled' 
        },
        'enrichment_export': { 
          pending: 'Preparing Export', 
          running: 'Exporting Enriched Data', 
          completed: 'Export Complete', 
          failed: 'Export Failed', 
          canceled: 'Export Canceled' 
        },
        'accounts_unlock': { 
          pending: 'Preparing Accounts', 
          running: 'Adding Accounts', 
          completed: 'Accounts Added', 
          failed: 'Add Accounts Failed', 
          canceled: 'Add Accounts Canceled' 
        },
      };
      return titles[job.job_type]?.[job.status] || 'Processing';
    };

    const getMessage = () => {
      if (job.error) return job.error;

      // FIXED: Use correct count based on job type and actual backend field names
      let recordCount = 0;
      if (job.job_type === 'contacts_unlock') {
        // Backend uses 'contacts_delivered' for unlock jobs
        recordCount = job.result?.contacts_delivered ?? 0;
      } else if (job.job_type === 'delete_contacts' || job.job_type === 'delete_accounts') {
        // Backend uses 'deleted_count' for delete jobs
        recordCount = job.result?.deleted_count ?? 0;
      } else if (job.job_type === 'remove_from_list') {
        // Backend uses 'removed_count' for remove from list jobs
        recordCount = job.result?.removed_count ?? 0;
      } else {
        recordCount = job.result?.record_count ?? job.result?.processed_count ?? 0;
      }
      
      switch (job.status) {
        case 'completed':
          if (recordCount > 0) {
            const action = job.job_type === 'contacts_unlock' ? 'unlocked' : 
                          job.job_type === 'delete_contacts' ? 'deleted' :
                          job.job_type === 'remove_from_list' ? 'removed' : 'processed';
            return `${recordCount.toLocaleString()} records ${action}`;
          }
          return 'Task completed successfully';
          
        case 'running':
          // FIXED: Use actual progress message from backend if available
          if (job.result?.message) {
            return job.result.message;
          }
          // For unlock jobs, show more specific progress
          if (job.job_type === 'contacts_unlock' && job.result) {
            const delivered = job.result.contacts_delivered || 0;
            const total = job.result.total_candidates || 0;
            if (total > 0 && delivered > 0) {
              return `Unlocking contacts: ${delivered.toLocaleString()} of ${total.toLocaleString()}`;
            } else if (total > 0) {
              return `Processing ${total.toLocaleString()} candidates...`;
            }
          }
          if (job.progress && job.progress > 0) {
            return getEstimatedTime() || `${Math.round(Math.min(100, job.progress))}% complete`;
          }
          return 'Processing your request';
          
        case 'pending':
          // FIXED: Use actual progress message from backend if available
          if (job.result?.message) {
            return job.result.message;
          }
          return 'Queued for processing';
          
        case 'failed':
          return job.error || 'An error occurred';
          
        case 'canceled':
          return 'Task was canceled';
          
        default:
          return 'Processing';
      }
    };

    const getEstimatedTime = () => {
      // Removed estimated time display as requested
      return null;
    };

    const getDuration = () => {
      if (job.completed_at && job.created_at) {
        const start = new Date(job.created_at).getTime();
        const end = new Date(job.completed_at).getTime();
        const duration = Math.round((end - start) / 1000);
        
        if (duration < 60) {
          return `${duration}s`;
        } else if (duration < 3600) {
          return `${Math.round(duration / 60)}m ${duration % 60}s`;
        }
      }
      return null;
    };

    return { 
      title: getTitle(), 
      message: getMessage(),
      estimatedTime: getEstimatedTime(),
      duration: getDuration()
    };
  }, [job]);

  const isActive = job.status === 'pending' || job.status === 'running';
  const isCompleted = job.status === 'completed';
  const canDownload = isCompleted && (job.job_type === 'export' || (job.job_type as string) === 'enrichment_export') && (job.result?.signed_download_url || job.result?.download_url);
  const hasProgress = job.progress != null && job.progress >= 0; // Show progress bar for any progress value >= 0

  return (
    <div className={`job-card ${isHiding ? 'hiding' : ''}`}>
      <div className="job-header">
        <div className="job-title-section">
          <h4 className="job-title">{jobInfo.title}</h4>
        </div>
        <div className="job-actions">
          {canDownload && (
            <button 
              className="btn-icon success"
              onClick={() => window.open(job.result?.signed_download_url || job.result?.download_url, '_blank')}
              title="Download file"
            >
              <Download size={16} />
            </button>
          )}
          
          {isActive ? (
            <button 
              onClick={handleCancel} 
              className="btn-icon"
              title="Cancel job"
            >
              <X size={16} />
            </button>
          ) : (
            <button 
              onClick={handleDismiss} 
              className="btn-icon"
              title="Dismiss"
            >
              <X size={16} />
            </button>
          )}
        </div>
      </div>
      
      {/* FIXED: Only show progress for active jobs with actual progress */}
      {isActive && hasProgress && (
        <div className="job-progress">
          <div className="progress-container">
            <div className="progress-header">
              <span className="progress-label">{Math.round(Math.min(100, job.progress))}%</span>
              {jobInfo.estimatedTime && (
                <span className="progress-meta">{jobInfo.estimatedTime}</span>
              )}
            </div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${Math.min(100, job.progress)}%` }} 
              />
            </div>
          </div>
        </div>
      )}

      {/* Completion status */}
      {(isCompleted || job.status === 'failed') && (
        <div className="job-completion">
          <span className={`completion-status ${job.status}`}>
            {job.status.toUpperCase()}
          </span>
          {jobInfo.duration && (
            <span className="completion-time">
              <Clock size={12} />
              {jobInfo.duration}
            </span>
          )}
        </div>
      )}

      {/* FIXED: Show correct metrics for each job type */}
      {isCompleted && (
        <div className="completion-details">
          {job.job_type === 'contacts_unlock' && (
            <>
              {(() => {
                const netNew = job.result?.net_new_contacts || 0;
                const refresh = job.result?.refresh_contacts || 0;
                const total = netNew + refresh;
                
                if (total > 0) {
                  return (
                    <>
                      <div className="detail-metric">
                        <span className="detail-value">
                          {total.toLocaleString()}
                        </span>
                        <span className="detail-label">Total</span>
                      </div>
                      {netNew > 0 && (
                        <div className="detail-metric">
                          <span className="detail-value">
                            {netNew.toLocaleString()}
                          </span>
                          <span className="detail-label">Net New</span>
                        </div>
                      )}
                      {refresh > 0 && (
                        <div className="detail-metric">
                          <span className="detail-value">
                            {refresh.toLocaleString()}
                          </span>
                          <span className="detail-label">Refresh</span>
                        </div>
                      )}
                    </>
                  );
                } else {
                  return (
                    <div className="detail-metric">
                      <span className="detail-value">0</span>
                      <span className="detail-label">Unlocked</span>
                    </div>
                  );
                }
              })()}
              {(job.result?.billing?.job_cost || job.result?.billing?.amount_spent) && (
                <div className="detail-metric">
                  <span className="detail-value">
                    ${(() => {
                      const cost = job.result.billing.job_cost || job.result.billing.amount_spent;
                      return parseFloat(String(cost || '0')).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    })()}
                  </span>
                  <span className="detail-label">Cost</span>
                </div>
              )}
              {job.result?.billing?.auto_charged && job.result?.billing?.charge_amount && (
                <div className="detail-metric">
                  <span className="detail-value billing-charged">
                    +${parseFloat(String(job.result.billing.charge_amount)).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                  <span className="detail-label">Auto-Charged</span>
                </div>
              )}
            </>
          )}
          
          {job.job_type === 'accounts_unlock' && (
            <>
              {(() => {
                const netNew = job.result?.accounts_added || 0;
                const refresh = job.result?.accounts_updated || 0;
                const total = netNew + refresh;
                
                if (total > 0) {
                  return (
                    <>
                      <div className="detail-metric">
                        <span className="detail-value">
                          {total.toLocaleString()}
                        </span>
                        <span className="detail-label">Total</span>
                      </div>
                      {netNew > 0 && (
                        <div className="detail-metric">
                          <span className="detail-value">
                            {netNew.toLocaleString()}
                          </span>
                          <span className="detail-label">Net New</span>
                        </div>
                      )}
                      {refresh > 0 && (
                        <div className="detail-metric">
                          <span className="detail-value">
                            {refresh.toLocaleString()}
                          </span>
                          <span className="detail-label">Refresh</span>
                        </div>
                      )}
                    </>
                  );
                } else {
                  return (
                    <div className="detail-metric">
                      <span className="detail-value">0</span>
                      <span className="detail-label">Unlocked</span>
                    </div>
                  );
                }
              })()}
              {(job.result?.billing?.job_cost || job.result?.billing?.amount_spent) && (
                <div className="detail-metric">
                  <span className="detail-value">
                    ${(() => {
                      const cost = job.result.billing.job_cost || job.result.billing.amount_spent;
                      return parseFloat(String(cost || '0')).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                    })()}
                  </span>
                  <span className="detail-label">Cost</span>
                </div>
              )}
            </>
          )}
          
          {job.job_type === 'export' && (
            <>
              <div className="detail-metric">
                <span className="detail-value">
                  {job.result?.record_count?.toLocaleString() || '0'}
                </span>
                <span className="detail-label">Records</span>
              </div>
              {job.result?.file_size_bytes && (
                <div className="detail-metric">
                  <span className="detail-value">
                    {formatFileSize(job.result.file_size_bytes)}
                  </span>
                  <span className="detail-label">Size</span>
                </div>
              )}
            </>
          )}
          
          {(job.job_type === 'delete_contacts' || job.job_type === 'delete_accounts' || job.job_type === 'add_to_list' || job.job_type === 'remove_from_list') && (
            <div className="detail-metric">
              <span className="detail-value">
                {job.job_type === 'delete_contacts' || job.job_type === 'delete_accounts'
                  ? (job.result?.deleted_count?.toLocaleString() || '0')
                  : job.job_type === 'remove_from_list'
                  ? (job.result?.removed_count?.toLocaleString() || '0')
                  : (job.result?.record_count?.toLocaleString() || '0')
                }
              </span>
              <span className="detail-label">
                {job.job_type === 'delete_contacts' || job.job_type === 'delete_accounts' ? 'Deleted' : 
                 job.job_type === 'add_to_list' ? 'Added' : 'Removed'}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
};

const ProgressTracker: React.FC = () => {
  const { state } = useAppContext();
  const activeJobs = state.activeJobs || [];
  

  if (activeJobs.length === 0) {
    return null;
  }

  return (
    <div className="progress-tracker">
      {activeJobs.map(job => (
        <JobCard key={job.job_id} job={job} />
      ))}
    </div>
  );
};

export default ProgressTracker;